<template>
  <app-content class="cashier-page">
    <Tabs v-model="activeTab" :datas="tabs" @change="onTabChange"></Tabs>
    
    <!-- 出纳账户 -->
    <div v-if="activeTab === 'account'" class="tab-content">
      <div class="toolbar">
        <Button color="primary" @click="openAccountModal()">新增账户</Button>
      </div>
      
      <Table :datas="accountList" :loading="loading">
        <TableItem title="账户编码" prop="code" align="center"></TableItem>
        <TableItem title="账户名称" prop="name" align="left"></TableItem>
        <TableItem title="类型" align="center">
          <template slot-scope="{data}">
            <span :class="'type-' + data.type">{{ getAccountTypeText(data.type) }}</span>
          </template>
        </TableItem>
        <TableItem title="卡号" prop="cardNumber" align="center"></TableItem>
        <TableItem title="对应科目" prop="subjectCode" align="center"></TableItem>
        <TableItem title="当前余额" align="right">
          <template slot-scope="{data}">
            <span class="balance">¥{{ formatMoney(data.balance) }}</span>
          </template>
        </TableItem>
        <TableItem title="操作" align="center">
          <template slot-scope="{data}">
            <Button text size="xs" @click="openAccountModal(data)">编辑</Button>
            <Button text size="xs" color="red" @click="deleteAccount(data)">删除</Button>
          </template>
        </TableItem>
      </Table>
    </div>

    <!-- 日记账 -->
    <div v-if="activeTab === 'journal'" class="tab-content">
      <div class="toolbar">
        <Button color="primary" @click="openJournalModal()">记一笔</Button>
        <Select v-model="selectedAccount" :datas="accountOptions" placeholder="选择账户" @change="loadJournals" />
        <DatePicker v-model="dateRange" type="daterange" placeholder="选择日期范围" @change="loadJournals" />
      </div>
      
      <Table :datas="journalList" :loading="loading">
        <TableItem title="日期" prop="billDate" align="center"></TableItem>
        <TableItem title="凭证号" prop="code" align="center"></TableItem>
        <TableItem title="摘要" prop="digest" align="left"></TableItem>
        <TableItem title="收支类型" align="center">
          <template slot-scope="{data}">
            <span :class="'journal-type-' + data.type">{{ getJournalTypeText(data.type) }}</span>
          </template>
        </TableItem>
        <TableItem title="借方金额" align="right">
          <template slot-scope="{data}">
            <span v-if="data.debit > 0" class="debit">¥{{ formatMoney(data.debit) }}</span>
          </template>
        </TableItem>
        <TableItem title="贷方金额" align="right">
          <template slot-scope="{data}">
            <span v-if="data.credit > 0" class="credit">¥{{ formatMoney(data.credit) }}</span>
          </template>
        </TableItem>
        <TableItem title="余额" align="right">
          <template slot-scope="{data}">
            <span class="balance">¥{{ formatMoney(data.balance) }}</span>
          </template>
        </TableItem>
        <TableItem title="操作" align="center">
          <template slot-scope="{data}">
            <Button text size="xs" @click="openJournalModal(data)">编辑</Button>
            <Button text size="xs" color="red" @click="deleteJournal(data)">删除</Button>
          </template>
        </TableItem>
      </Table>
    </div>

    <!-- 资金调拨 -->
    <div v-if="activeTab === 'transfer'" class="tab-content">
      <div class="toolbar">
        <Button color="primary" @click="openTransferModal()">资金调拨</Button>
      </div>
      
      <Table :datas="transferList" :loading="loading">
        <TableItem title="调拨日期" prop="transferDate" align="center"></TableItem>
        <TableItem title="转出账户" align="center">
          <template slot-scope="{data}">
            {{ getAccountName(data.outAccountId) }}
          </template>
        </TableItem>
        <TableItem title="转入账户" align="center">
          <template slot-scope="{data}">
            {{ getAccountName(data.inAccountId) }}
          </template>
        </TableItem>
        <TableItem title="调拨金额" align="right">
          <template slot-scope="{data}">
            <span class="amount">¥{{ formatMoney(data.amount) }}</span>
          </template>
        </TableItem>
        <TableItem title="摘要" prop="digest" align="left"></TableItem>
        <TableItem title="备注" prop="remark" align="left"></TableItem>
        <TableItem title="操作" align="center">
          <template slot-scope="{data}">
            <Button text size="xs" color="red" @click="deleteTransfer(data)">删除</Button>
          </template>
        </TableItem>
      </Table>
    </div>

    <!-- 收支类型 -->
    <div v-if="activeTab === 'ioType'" class="tab-content">
      <div class="toolbar">
        <Button color="primary" @click="openIoTypeModal()">新增类型</Button>
      </div>
      
      <Table :datas="ioTypeList" :loading="loading">
        <TableItem title="类型编码" prop="code" align="center"></TableItem>
        <TableItem title="类型名称" prop="name" align="left"></TableItem>
        <TableItem title="收支类别" align="center">
          <template slot-scope="{data}">
            <span :class="'io-type-' + data.type">{{ getIoTypeText(data.type) }}</span>
          </template>
        </TableItem>
        <TableItem title="操作" align="center">
          <template slot-scope="{data}">
            <Button text size="xs" @click="openIoTypeModal(data)">编辑</Button>
            <Button text size="xs" color="red" @click="deleteIoType(data)">删除</Button>
          </template>
        </TableItem>
      </Table>
    </div>

    <!-- 账户弹窗 -->
    <Modal v-model="showAccountModal" :hasCloseIcon="true" :width="760" class="cashier-modal">
      <div class="modal-header" :class="accountForm.id ? 'modal-header-edit' : 'modal-header-create'">
        <div class="modal-title">{{ accountForm.id ? '编辑账户' : '新增账户' }}</div>
        <div class="modal-subtitle">维护现金、银行账户和对应科目信息</div>
      </div>
      <Form ref="accountForm" :model="accountForm" :rules="accountRules" labelPosition="right" :labelWidth="110" class="cashier-form">
        <div class="form-row">
          <FormItem label="账户编码" prop="code" class="form-col">
            <input v-model="accountForm.code" placeholder="请输入账户编码" />
          </FormItem>
          <FormItem label="账户名称" prop="name" class="form-col">
            <input v-model="accountForm.name" placeholder="请输入账户名称" />
          </FormItem>
        </div>
        <div class="form-row">
          <FormItem label="账户类型" prop="type" class="form-col">
            <Select v-model="accountForm.type" :datas="accountTypeOptions" placeholder="请选择账户类型" />
          </FormItem>
          <FormItem label="卡号" class="form-col">
            <input v-model="accountForm.cardNumber" placeholder="请输入卡号（可选）" />
          </FormItem>
        </div>
        <FormItem label="对应科目" class="form-full">
          <input v-model="accountForm.subjectCode" placeholder="请输入对应科目编码" />
        </FormItem>
      </Form>
      <div slot="footer">
        <Button color="primary" @click="saveAccount">保存</Button>
        <Button @click="showAccountModal = false">取消</Button>
      </div>
    </Modal>

    <!-- 日记账弹窗 -->
    <Modal v-model="showJournalModal" :hasCloseIcon="true" :width="760" class="cashier-modal">
      <div class="modal-header" :class="journalForm.id ? 'modal-header-edit' : 'modal-header-create'">
        <div class="modal-title">{{ journalForm.id ? '编辑记录' : '记一笔' }}</div>
        <div class="modal-subtitle">登记账户收款、付款流水</div>
      </div>
      <Form ref="journalForm" :model="journalForm" :rules="journalRules" labelPosition="right" :labelWidth="110" class="cashier-form">
        <div class="form-row">
          <FormItem label="账户" prop="accountId" class="form-col">
            <Select v-model="journalForm.accountId" :datas="accountOptions" placeholder="请选择账户" />
          </FormItem>
          <FormItem label="日期" prop="billDate" class="form-col">
            <DatePicker v-model="journalForm.billDate" type="date" placeholder="请选择日期" />
          </FormItem>
        </div>
        <div class="form-row">
          <FormItem label="类型" prop="type" class="form-col">
            <Select v-model="journalForm.type" :datas="journalTypeOptions" placeholder="请选择类型" />
          </FormItem>
          <FormItem label="金额" prop="amount" class="form-col">
            <input v-model.number="journalForm.amount" type="number" placeholder="请输入金额" />
          </FormItem>
        </div>
        <div class="form-row">
          <FormItem label="摘要" prop="digest" class="form-col">
            <input v-model="journalForm.digest" placeholder="请输入摘要" />
          </FormItem>
          <FormItem label="备注" class="form-col">
            <input v-model="journalForm.remark" placeholder="请输入备注（可选）" />
          </FormItem>
        </div>
      </Form>
      <div slot="footer">
        <Button color="primary" @click="saveJournal">保存</Button>
        <Button @click="showJournalModal = false">取消</Button>
      </div>
    </Modal>

    <!-- 资金调拨弹窗 -->
    <Modal v-model="showTransferModal" :hasCloseIcon="true" :width="760" class="cashier-modal">
      <div class="modal-header modal-header-create">
        <div class="modal-title">资金调拨</div>
        <div class="modal-subtitle">在出纳账户之间转移资金</div>
      </div>
      <Form ref="transferForm" :model="transferForm" :rules="transferRules" labelPosition="right" :labelWidth="110" class="cashier-form">
        <div class="form-row">
          <FormItem label="转出账户" prop="outAccountId" class="form-col">
            <Select v-model="transferForm.outAccountId" :datas="accountOptions" placeholder="请选择转出账户" />
          </FormItem>
          <FormItem label="转入账户" prop="inAccountId" class="form-col">
            <Select v-model="transferForm.inAccountId" :datas="accountOptions" placeholder="请选择转入账户" />
          </FormItem>
        </div>
        <div class="form-row">
          <FormItem label="调拨金额" prop="amount" class="form-col">
            <input v-model.number="transferForm.amount" type="number" placeholder="请输入调拨金额" />
          </FormItem>
          <FormItem label="调拨日期" prop="transferDate" class="form-col">
            <DatePicker v-model="transferForm.transferDate" type="date" placeholder="请选择调拨日期" />
          </FormItem>
        </div>
        <div class="form-row">
          <FormItem label="摘要" prop="digest" class="form-col">
            <input v-model="transferForm.digest" placeholder="请输入摘要" />
          </FormItem>
          <FormItem label="备注" class="form-col">
            <input v-model="transferForm.remark" placeholder="请输入备注（可选）" />
          </FormItem>
        </div>
      </Form>
      <div slot="footer">
        <Button color="primary" @click="saveTransfer">保存</Button>
        <Button @click="showTransferModal = false">取消</Button>
      </div>
    </Modal>

    <!-- 收支类型弹窗 -->
    <Modal v-model="showIoTypeModal" :hasCloseIcon="true" :width="760" class="cashier-modal">
      <div class="modal-header" :class="ioTypeForm.id ? 'modal-header-edit' : 'modal-header-create'">
        <div class="modal-title">{{ ioTypeForm.id ? '编辑类型' : '新增类型' }}</div>
        <div class="modal-subtitle">维护出纳日记账的收支分类</div>
      </div>
      <Form ref="ioTypeForm" :model="ioTypeForm" :rules="ioTypeRules" labelPosition="right" :labelWidth="110" class="cashier-form">
        <div class="form-row">
          <FormItem label="类型编码" prop="code" class="form-col">
            <input v-model="ioTypeForm.code" placeholder="请输入类型编码" />
          </FormItem>
          <FormItem label="类型名称" prop="name" class="form-col">
            <input v-model="ioTypeForm.name" placeholder="请输入类型名称" />
          </FormItem>
        </div>
        <FormItem label="收支类别" prop="type" class="form-full">
          <Select v-model="ioTypeForm.type" :datas="ioTypeCategoryOptions" placeholder="请选择收支类别" />
        </FormItem>
      </Form>
      <div slot="footer">
        <Button color="primary" @click="saveIoType">保存</Button>
        <Button @click="showIoTypeModal = false">取消</Button>
      </div>
    </Modal>
  </app-content>
</template>

<script>
import cashierApi from '../../api/cashier';

export default {
  name: 'Cashier',
  data() {
    return {
      activeTab: 'account',
      tabs: [
        { title: '出纳账户', key: 'account' },
        { title: '日记账', key: 'journal' },
        { title: '资金调拨', key: 'transfer' },
        { title: '收支类型', key: 'ioType' }
      ],
      loading: false,
      
      // 账户相关
      accountList: [],
      showAccountModal: false,
      accountForm: { id: null, code: '', name: '', type: '', cardNumber: '', subjectCode: '' },
      accountRules: {
        code: [{ required: true, message: '请输入账户编码' }],
        name: [{ required: true, message: '请输入账户名称' }],
        type: [{ required: true, message: '请选择账户类型' }]
      },
      accountTypeOptions: [],
      
      // 日记账相关
      journalList: [],
      selectedAccount: null,
      dateRange: null,
      showJournalModal: false,
      journalForm: { id: null, accountId: null, billDate: null, type: '', amount: 0, digest: '', remark: '' },
      journalRules: {
        accountId: [{ required: true, message: '请选择账户' }],
        billDate: [{ required: true, message: '请选择日期' }],
        type: [{ required: true, message: '请选择类型' }],
        amount: [{ required: true, message: '请输入金额' }],
        digest: [{ required: true, message: '请输入摘要' }]
      },
      journalTypeOptions: [],
      
      // 调拨相关
      transferList: [],
      showTransferModal: false,
      transferForm: { id: null, outAccountId: null, inAccountId: null, amount: 0, transferDate: null, digest: '', remark: '' },
      transferRules: {
        outAccountId: [{ required: true, message: '请选择转出账户' }],
        inAccountId: [{ required: true, message: '请选择转入账户' }],
        amount: [{ required: true, message: '请输入调拨金额' }],
        transferDate: [{ required: true, message: '请选择调拨日期' }],
        digest: [{ required: true, message: '请输入摘要' }]
      },
      
      // 收支类型相关
      ioTypeList: [],
      showIoTypeModal: false,
      ioTypeForm: { id: null, code: '', name: '', type: '' },
      ioTypeRules: {
        code: [{ required: true, message: '请输入类型编码' }],
        name: [{ required: true, message: '请输入类型名称' }],
        type: [{ required: true, message: '请选择收支类别' }]
      },
      ioTypeCategoryOptions: []
    };
  },
  computed: {
    accountOptions() {
      return this.accountList.map(a => ({ title: a.name, key: a.id }));
    }
  },
  mounted() {
    this.loadAccounts();
    this.loadEnums();
  },
  methods: {
    onTabChange(tab) {
      this.activeTab = tab.key;
      if (tab.key === 'account') this.loadAccounts();
      if (tab.key === 'journal') this.loadJournals();
      if (tab.key === 'transfer') this.loadTransfers();
      if (tab.key === 'ioType') this.loadIoTypes();
    },
    
    // 加载枚举
    async loadEnums() {
      try {
        const [accountTypes, journalTypes, ioTypes] = await Promise.all([
          cashierApi.getCashierAccountTypes(),
          cashierApi.getCashierJournalTypes(),
          cashierApi.getCashierIoTypeEnums()
        ]);
        this.accountTypeOptions = (accountTypes.data || []).map(t => ({ title: t.label, key: t.value }));
        this.journalTypeOptions = (journalTypes.data || []).map(t => ({ title: t.label, key: t.value }));
        this.ioTypeCategoryOptions = (ioTypes.data || []).map(t => ({ title: t.label, key: t.value }));
      } catch (e) {
        console.error('加载枚举失败', e);
      }
    },
    
    // 账户相关
    async loadAccounts() {
      this.loading = true;
      try {
        const res = await cashierApi.getCashierAccounts();
        this.accountList = res.data || [];
      } finally {
        this.loading = false;
      }
    },
    openAccountModal(data = null) {
      if (data) {
        this.accountForm = { ...data };
      } else {
        this.accountForm = { id: null, code: '', name: '', type: '', cardNumber: '', subjectCode: '' };
      }
      this.showAccountModal = true;
    },
    async saveAccount() {
      const validResult = await this.$refs.accountForm.valid();
      const valid = typeof validResult === 'boolean' ? validResult : validResult && validResult.result;
      if (!valid) {
        this.$Message.error('请完善账户必填信息');
        return;
      }
      try {
        if (this.accountForm.id) {
          await cashierApi.updateCashierAccount(this.accountForm.id, this.accountForm);
        } else {
          await cashierApi.createCashierAccount(this.accountForm);
        }
        this.showAccountModal = false;
        this.$Message.success('保存成功');
        this.loadAccounts();
      } catch (e) {
        this.$Message.error(e.msg || '保存失败');
      }
    },
    async deleteAccount(data) {
      if (!confirm(`确认删除账户 ${data.name}？`)) return;
      try {
        await cashierApi.deleteCashierAccount(data.id);
        this.$Message.success('删除成功');
        this.loadAccounts();
      } catch (e) {
        this.$Message.error(e.msg || '删除失败');
      }
    },
    getAccountTypeText(type) {
      const map = { Cash: '现金', Bank: '银行' };
      return map[type] || type;
    },
    getAccountName(id) {
      const account = this.accountList.find(a => a.id === id);
      return account ? account.name : '-';
    },
    
    // 日记账相关
    async loadJournals() {
      if (!this.selectedAccount) return;
      this.loading = true;
      try {
        const startDate = this.dateRange && this.dateRange[0] ? this.formatDate(this.dateRange[0]) : '';
        const endDate = this.dateRange && this.dateRange[1] ? this.formatDate(this.dateRange[1]) : '';
        const res = await cashierApi.getCashierJournalsByAccount(this.selectedAccount, startDate, endDate);
        this.journalList = res.data || [];
      } finally {
        this.loading = false;
      }
    },
    openJournalModal(data = null) {
      if (data) {
        this.journalForm = { ...data };
      } else {
        this.journalForm = { id: null, accountId: this.selectedAccount, billDate: new Date(), type: '', amount: 0, digest: '', remark: '' };
      }
      this.showJournalModal = true;
    },
    async saveJournal() {
      const validResult = await this.$refs.journalForm.valid();
      const valid = typeof validResult === 'boolean' ? validResult : validResult && validResult.result;
      if (!valid) {
        this.$Message.error('请完善日记账必填信息');
        return;
      }
      try {
        const params = { ...this.journalForm };
        if (params.type === 'Receipt') {
          params.debit = params.amount;
          params.credit = 0;
        } else {
          params.debit = 0;
          params.credit = params.amount;
        }
        delete params.amount;
        
        if (params.id) {
          await cashierApi.updateCashierJournal(params.id, params);
        } else {
          await cashierApi.createCashierJournal(params);
        }
        this.showJournalModal = false;
        this.$Message.success('保存成功');
        this.loadJournals();
        this.loadAccounts();
      } catch (e) {
        this.$Message.error(e.msg || '保存失败');
      }
    },
    async deleteJournal(data) {
      if (!confirm('确认删除该记录？')) return;
      try {
        await cashierApi.deleteCashierJournal(data.id);
        this.$Message.success('删除成功');
        this.loadJournals();
        this.loadAccounts();
      } catch (e) {
        this.$Message.error(e.msg || '删除失败');
      }
    },
    getJournalTypeText(type) {
      const map = { Receipt: '收款', Payment: '付款' };
      return map[type] || type;
    },
    
    // 调拨相关
    async loadTransfers() {
      this.loading = true;
      try {
        const res = await cashierApi.getCashierTransfers();
        this.transferList = res.data || [];
      } finally {
        this.loading = false;
      }
    },
    openTransferModal() {
      this.transferForm = { id: null, outAccountId: null, inAccountId: null, amount: 0, transferDate: new Date(), digest: '', remark: '' };
      this.showTransferModal = true;
    },
    async saveTransfer() {
      const validResult = await this.$refs.transferForm.valid();
      const valid = typeof validResult === 'boolean' ? validResult : validResult && validResult.result;
      if (!valid) {
        this.$Message.error('请完善调拨必填信息');
        return;
      }
      if (this.transferForm.outAccountId === this.transferForm.inAccountId) {
        this.$Message.error('转出账户和转入账户不能相同');
        return;
      }
      try {
        await cashierApi.executeCashierTransfer(this.transferForm);
        this.showTransferModal = false;
        this.$Message.success('调拨成功');
        this.loadTransfers();
        this.loadAccounts();
      } catch (e) {
        this.$Message.error(e.msg || '调拨失败');
      }
    },
    async deleteTransfer(data) {
      if (!confirm('确认删除该调拨记录？')) return;
      try {
        await cashierApi.deleteCashierTransfer(data.id);
        this.$Message.success('删除成功');
        this.loadTransfers();
        this.loadAccounts();
      } catch (e) {
        this.$Message.error(e.msg || '删除失败');
      }
    },
    
    // 收支类型相关
    async loadIoTypes() {
      this.loading = true;
      try {
        const res = await cashierApi.getCashierIoTypes();
        this.ioTypeList = res.data || [];
      } finally {
        this.loading = false;
      }
    },
    openIoTypeModal(data = null) {
      if (data) {
        this.ioTypeForm = { ...data };
      } else {
        this.ioTypeForm = { id: null, code: '', name: '', type: '' };
      }
      this.showIoTypeModal = true;
    },
    async saveIoType() {
      const validResult = await this.$refs.ioTypeForm.valid();
      const valid = typeof validResult === 'boolean' ? validResult : validResult && validResult.result;
      if (!valid) {
        this.$Message.error('请完善收支类型必填信息');
        return;
      }
      try {
        if (this.ioTypeForm.id) {
          await cashierApi.updateCashierIoType(this.ioTypeForm.id, this.ioTypeForm);
        } else {
          await cashierApi.createCashierIoType(this.ioTypeForm);
        }
        this.showIoTypeModal = false;
        this.$Message.success('保存成功');
        this.loadIoTypes();
      } catch (e) {
        this.$Message.error(e.msg || '保存失败');
      }
    },
    async deleteIoType(data) {
      if (!confirm(`确认删除类型 ${data.name}？`)) return;
      try {
        await cashierApi.deleteCashierIoType(data.id);
        this.$Message.success('删除成功');
        this.loadIoTypes();
      } catch (e) {
        this.$Message.error(e.msg || '删除失败');
      }
    },
    getIoTypeText(type) {
      const map = { Income: '收入', Expense: '支出' };
      return map[type] || type;
    },
    
    // 工具方法
    formatMoney(value) {
      if (value === null || value === undefined) return '0.00';
      return parseFloat(value).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    },
    formatDate(date) {
      if (!date) return '';
      const d = new Date(date);
      return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    }
  }
};
</script>

<style scoped>
.cashier-page {
  padding: 20px;
}
.tab-content {
  margin-top: 16px;
}
.toolbar {
  margin-bottom: 16px;
}
.toolbar button, .toolbar .h-select, .toolbar .h-datepicker {
  margin-right: 8px;
}
.type-Cash { color: #52c41a; }
.type-Bank { color: #1890ff; }
.journal-type-Receipt { color: #52c41a; }
.journal-type-Payment { color: #f5222d; }
.io-type-Income { color: #52c41a; }
.io-type-Expense { color: #f5222d; }
.balance { color: #1890ff; font-weight: 500; }
.debit { color: #52c41a; }
.credit { color: #f5222d; }
.amount { color: #faad14; font-weight: 500; }

/* 弹窗头部标题优化 */
.modal-header {
  margin: -20px -20px 10px -20px;
  padding: 12px 20px;
}
.modal-header-create {
  background: linear-gradient(135deg, #1890ff 0%, #096dd9 100%);
}
.modal-header-edit {
  background: linear-gradient(135deg, #722ed1 0%, #531dab 100%);
}
.modal-title {
  font-size: 16px;
  font-weight: 600;
  color: #fff;
  line-height: 1.3;
}
.modal-subtitle {
  font-size: 12px;
  color: rgba(255, 255, 255, 0.78);
  margin-top: 1px;
}

/* 表单样式优化 */
.cashier-form {
  padding-bottom: 6px;
}
.form-row {
  display: flex;
  gap: 14px;
  margin-bottom: 2px;
  align-items: flex-start;
}
.form-col {
  flex: 1;
  margin-bottom: 0;
}
.form-full {
  margin-bottom: 0;
}
.form-col :deep(.h-form-item),
.form-full:deep(.h-form-item) {
  margin-bottom: 0 !important;
  padding-top: 0 !important;
  padding-bottom: 0 !important;
  display: flex;
  align-items: flex-start;
  flex-wrap: nowrap;
  min-height: 40px;
}
.form-col :deep(.h-form-item-content),
.form-full:deep(.h-form-item-content) {
  flex: 1;
  min-width: 0;
}
.form-col :deep(.h-form-item-wrap),
.form-full:deep(.h-form-item-wrap) {
  min-height: 34px;
  display: flex;
  align-items: center;
}
.form-col :deep(.h-form-item-error),
.form-full:deep(.h-form-item-error) {
  margin-top: 1px;
  line-height: 14px;
  color: #f5222d;
  font-size: 12px;
}
.form-col :deep(.h-form-item-error-label),
.form-full:deep(.h-form-item-error-label) {
  display: none;
}
.form-col :deep(.h-form-item-label),
.form-full:deep(.h-form-item-label) {
  width: 108px;
  height: 34px;
  padding-right: 8px;
  font-size: 14px;
  font-weight: normal;
  color: #595959;
  line-height: 34px;
  text-align: right;
  white-space: nowrap;
  flex-shrink: 0;
}
.form-col :deep(.h-form-item-required .h-form-item-label::before),
.form-full:deep(.h-form-item-required .h-form-item-label::before) {
  content: '* ';
  color: #ff4d4f;
  margin-right: 2px;
}
.form-col input,
.form-full input,
.form-col :deep(.h-select),
.form-full:deep(.h-select),
.form-col :deep(.h-datetime),
.form-col :deep(.h-datetime-show) {
  width: 100%;
  height: 34px;
  font-size: 14px;
  flex: 1;
  box-sizing: border-box;
}
.form-col input,
.form-full input,
.form-col :deep(.h-datetime-show) {
  border-radius: 6px;
  border: 1px solid #d9d9d9;
  padding: 0 10px;
  transition: all 0.2s ease;
  background: #fafafa;
  color: #333;
}
.form-col input:hover,
.form-full input:hover,
.form-col :deep(.h-datetime-show:hover) {
  border-color: #40a9ff;
  background: #fff;
}
.form-col input:focus,
.form-full input:focus,
.form-col :deep(.h-datetime-show:focus) {
  border-color: #1890ff;
  box-shadow: 0 0 0 2px rgba(24, 144, 255, 0.1);
  background: #fff;
  outline: none;
}
.form-col :deep(.h-select .h-select-show),
.form-full:deep(.h-select .h-select-show) {
  height: 34px !important;
  border-radius: 6px;
  border: 1px solid #d9d9d9;
  transition: all 0.2s ease;
  background: #fafafa;
  box-sizing: border-box;
}
.form-col :deep(.h-select .h-select-show:hover),
.form-full:deep(.h-select .h-select-show:hover) {
  border-color: #40a9ff;
  background: #fff;
}
.form-col :deep(.h-select .h-select-show.h-select-focus),
.form-full:deep(.h-select .h-select-show.h-select-focus) {
  border-color: #1890ff;
  box-shadow: 0 0 0 2px rgba(24, 144, 255, 0.1);
  background: #fff;
}
.form-col :deep(.h-form-item-valid-error) input,
.form-full:deep(.h-form-item-valid-error) input,
.form-col :deep(.h-form-item-valid-error .h-select-show),
.form-full:deep(.h-form-item-valid-error .h-select-show),
.form-col :deep(.h-form-item-valid-error .h-datetime-show) {
  border-color: #f5222d;
  background: #fffafa;
}

/* 弹窗整体样式优化 */
:deep(.cashier-modal .h-modal) {
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.15);
}
:deep(.cashier-modal .h-modal-body) {
  max-height: none;
  overflow-y: visible;
  padding: 0 20px 0 20px;
}
:deep(.cashier-modal .h-modal-footer) {
  border-top: 1px solid #f0f0f0;
  padding: 8px 20px;
  background: #fafafa;
  display: flex;
  justify-content: center;
  gap: 12px;
}
:deep(.cashier-modal .h-modal-footer .h-btn) {
  padding: 0 24px;
  height: 34px;
  font-size: 14px;
  border-radius: 6px;
  font-weight: 500;
  transition: all 0.2s ease;
  min-width: 96px;
}
:deep(.cashier-modal .h-modal-footer .h-btn-primary) {
  background: linear-gradient(135deg, #1890ff, #096dd9);
  border: none;
  box-shadow: 0 4px 12px rgba(24, 144, 255, 0.35);
}
:deep(.cashier-modal .h-modal-footer .h-btn-primary:hover) {
  background: linear-gradient(135deg, #40a9ff, #1890ff);
  box-shadow: 0 6px 16px rgba(24, 144, 255, 0.45);
  transform: translateY(-1px);
}
:deep(.cashier-modal .h-modal-footer .h-btn:not(.h-btn-primary)) {
  background: #fff;
  border: 1px solid #d9d9d9;
  color: #595959;
}
:deep(.cashier-modal .h-modal-footer .h-btn:not(.h-btn-primary):hover) {
  border-color: #1890ff;
  color: #1890ff;
}
</style>
